/* eslint-disable @typescript-eslint/no-explicit-any */

import { AjaxActions, RequestOptionsTemplate } from '../utils/AjaxActions';
import { filter, get, includes, isEmpty, map, omit } from 'es-toolkit/compat';

// A Redux middleware that interprets actions with Tock info specified.
// Performs the call and promises when such actions are dispatched.
export function createTockApiMiddleware(options: any) {
  let ajaxActions: AjaxActions;
  let getAuthState: any;
  if (options.apiClient && options.apiClient instanceof AjaxActions) {
    ajaxActions = options.apiClient;
    getAuthState = options.getAuthState;
  } else {
    getAuthState = options.getAuthState;
    ajaxActions = new AjaxActions(omit(options, 'getAuthState'));
  }
  // A bunch of variables related to tracking and handle dynamic batches
  // activeBatch: Only one can exist at a given time. If a batch is called inside a batch
  // the inner calls of the inner batch just get pushed into the single dynamic batch
  // Is null when no batch is active, is an array when a batch is active
  let activeBatch: any[] | null = null;
  // numOpenBatches: A counter for the number of open batches, so that batches
  // can be called within batches without barfing. We commit the dynamic batch
  // when the counter reaches zero.
  let numOpenBatches = 0;
  // activeBatchPromise: The promise to be returned by any call to endBatch.
  // By having a shared promise it ensures that inner batches can be resolved
  // at the same time as the outermost batch.
  // Is only resolved once after the last endBatch call
  let activeBatchPromise: any = null;
  // activeBatchResolver: The resolve function for the activeBatchPromise.
  // Needs to be exposed outside of the promise in order for us to
  // resolve said promise later, outside of the Promise's executor.
  let activeBatchResolver: any = null;
  // activeBatchRejecter: The reject function for the activeBatchPromise.
  // Needs to be exposed outside of the promise in order for us to
  // reject said promise later, outside of the Promise's executor.
  let activeBatchRejecter: any = null;

  return (store: any) => (next: any) => (action: any) => {
    const callAPI: AnyApiAction | BatchAction = action.TockApi;
    if (!callAPI) {
      return next(action);
    }
    const state = store.getState();
    const appState = getAuthState(state);

    const variantMap: Dictionary<{ name: string; variant: number }> =
      state?.experiments?.variants || {};
    const experimentVariantHeader = Object.values(variantMap)
      .map((variantConfig) => {
        return `${variantConfig?.name}:${variantConfig?.variant}`;
      })
      .join(',');

    const selectedMetroAreaId = state?.metroArea?.selectedMetroArea?.id;

    if ((callAPI as BatchBeginAction).beginBatch) {
      numOpenBatches++;
      if (activeBatch) {
        return null;
      }
      activeBatch = [];
      activeBatchPromise = new Promise((resolve, reject) => {
        activeBatchResolver = resolve;
        activeBatchRejecter = reject;
      });
      return null;
    } else if ((callAPI as BatchEndAction).endBatch) {
      numOpenBatches--;
      if (numOpenBatches > 0) {
        return activeBatchPromise;
      }
      // If the batch is empty when it's closed, just return a resolved promise
      if (isEmpty(activeBatch)) {
        activeBatch = null;
        const endPromise = activeBatchPromise;
        activeBatchPromise = null;
        activeBatchResolver();
        return endPromise;
      }
      const batchToSend = map(activeBatch as any[], (request: any) => {
        const methodProto = getMethod(request);
        const method = getHttpMethod(methodProto);
        return {
          action: request,
          method,
          path: request[methodProto],
          payload: request.request,
          requestScope: request.requestScope,
        };
      });
      const batchOptions = {
        app: appState,
        type: options.appConstants.DYNAMIC_BATCH,
      };
      const dynamicBatchPromise = ajaxActions.dynamicBatch(batchToSend, batchOptions)(next);
      const endPromise = activeBatchPromise;
      activeBatch = null;
      activeBatchPromise = null;
      // These cannot be arrow functions if we want to pass arguments, because otherwise
      // babel thinks we're talking about the arguments of the closure
      dynamicBatchPromise.then(
        function (...args: any[]) {
          activeBatchResolver(...args);
          return args;
        },
        function (...args: any[]) {
          activeBatchRejecter(...args);
          return args;
        }
      );
      return endPromise;
    }

    const {
      type,
      toast,
      onSuccess,
      onError,
      scope,
      context,
      cacheOptions,
      discardOldRequests,
      requestScope,
      ignoreNetworkErrors,
    } = callAPI as AnyApiAction;

    if (!type) {
      throw new Error('Type is not specified, action ' + JSON.stringify(action));
    }

    if (activeBatch) {
      if ((callAPI as BatchApiAction).batchProto) {
        throw new Error(
          'There is an active batch. Batch requests cannot be nested in active batches'
        );
      }
      activeBatch.push(callAPI);
      return null; // Altervatively return a rejected promise
    }

    const shippingState = state.app.shippingState;
    const syndicate = get(store.getState(), 'syndicate.syndicate');

    try {
      const requestOptions: Omit<RequestOptionsTemplate, 'path'> = {
        app: appState,
        experimentVariantHeader,
        cacheOptions,
        context,
        errorMapper: onError,
        metroAreaId: selectedMetroAreaId,
        scope,
        shippingState,
        successMessage: toast,
        syndicate,
        type,
        discardOldRequests,
        requestScope,
        ignoreNetworkErrors,
        onSuccess: onSuccess
          ? (value: any) => {
              onSuccess(store.getState(), value);
            }
          : undefined,
      };
      if ((callAPI as AnyApiAction).responseType !== undefined) {
        requestOptions.responseProtoType = (callAPI as AnyApiAction).responseType;
      }

      if ((callAPI as BatchApiAction).batchProto) {
        if (!Array.isArray((callAPI as BatchApiAction).batchProto)) {
          throw new Error('Batch proto request must be an array with requests');
        }
        // TODO: context not currently supported for batches. Will need to rework the batch function to support
        // context on a per-request basis
        return ajaxActions.batchProto((callAPI as BatchApiAction).batchProto, requestOptions)(next);
      }

      const method = getMethod(callAPI as AnyApiAction);
      switch (method) {
        case 'getProto':
          return ajaxActions.getProto({
            path: (callAPI as GetApiAction).getProto,
            ...requestOptions,
          })(next);
        case 'getListProto':
          return ajaxActions.getListProto({
            path: (callAPI as GetListApiAction).getListProto,
            ...requestOptions,
          })(next);
        case 'getFile':
          return ajaxActions.getFile({
            path: (callAPI as GetFileApiAction).getFile,
            ...requestOptions,
          })(next);
        case 'getRawResponse':
          return ajaxActions.getRawResponse({
            path: (callAPI as GetRawResponseApiAction).getRawResponse,
            ...requestOptions,
          })(next);
        case 'postProto':
          return ajaxActions.postProto((callAPI as PostApiAction).request, {
            path: (callAPI as PostApiAction).postProto,
            ...requestOptions,
          })(next);
        case 'postListProto':
          return ajaxActions.postListProto((callAPI as PostListApiAction).request, {
            path: (callAPI as PostListApiAction).postListProto,
            ...requestOptions,
          })(next);
        case 'postProtoForFile':
          return ajaxActions.postProtoForFile((callAPI as PostApiAction).request, {
            path: (callAPI as PostProtoForFileApiAction).postProtoForFile,
            ...requestOptions,
          })(next);
        case 'postFile':
          return ajaxActions.postFile((callAPI as PostFileApiAction).request, {
            path: (callAPI as PostFileApiAction).postFile,
            ...requestOptions,
          })(next);
        case 'putProto':
          return ajaxActions.putProto((callAPI as PutApiAction).request, {
            path: (callAPI as PutApiAction).putProto,
            ...requestOptions,
          })(next);
        case 'putListProto':
          return ajaxActions.putListProto((callAPI as PutListApiAction).request, {
            path: (callAPI as PutListApiAction).putListProto,
            ...requestOptions,
          })(next);
        case 'deleteProto':
          return ajaxActions.deleteProto({
            path: (callAPI as DeleteApiAction).deleteProto,
            ...requestOptions,
          })(next);
        case 'deleteListProto':
          return ajaxActions.deleteListProto((callAPI as DeleteListApiAction).request, {
            path: (callAPI as DeleteListApiAction).deleteListProto,
            ...requestOptions,
          })(next);
        default:
          throw new Error('You may have added a new ajax method, please add it to TockApi');
      }
    } catch (e) {
      //eslint-disable-next-line no-console
      console.error(e);
    }
  };
}

function getMethod(callAPI: AnyApiAction): string {
  const methods = filter(
    Object.keys(callAPI),
    (key: any) =>
      (key !== 'responseProtoType' && includes(key, 'Proto')) ||
      includes(key, 'postFile') ||
      includes(key, 'getFile') ||
      includes(key, 'getRawResponse')
  );
  if (methods.length !== 1) {
    throw new Error('Only one method must be specified ' + JSON.stringify(methods));
  }
  const method = methods[0];
  if (typeof (callAPI as any)[method] !== 'string') {
    throw new Error('Specify a string endpoint URL.');
  }
  return method;
}

function getHttpMethod(methodProto: string): string {
  let method;
  if (includes(methodProto, 'get')) {
    method = 'GET';
  } else if (includes(methodProto, 'post')) {
    method = 'POST';
  } else if (includes(methodProto, 'put')) {
    method = 'PUT';
  } else if (includes(methodProto, 'delete')) {
    method = 'DELETE';
  } else {
    throw new Error(`${methodProto} doesn't seem to be a GET/POST/PUT/DELETE request`);
  }
  return method;
}
